// What each file of a library upload is doing right now, as a row the shelf
// can show — pure, from the hook's phase, the session it opened and the
// state of every put. "Failed" carries why: a file that did not arrive
// must say what refused it, not just that it did.

import type { ClipsSession } from "./library-upload";

export type ClipFileStatus =
  | "waiting"
  | "listening"
  | "converting"
  | "queued"
  | "sending"
  | "sent"
  | "already"
  | "checking"
  | "added"
  | "failed";

export interface ClipFileView {
  /** The job's pool key when there is one, else the file's name. */
  id: string;
  label: string;
  bytes: number;
  sent: number;
  status: ClipFileStatus;
  /** For a failed file: the put's own words. */
  reason?: string;
}

export interface ClipJobState {
  status: "queued" | "sending" | "sent" | "failed";
  sent: number;
  reason?: string;
}

export type ClipsPhaseKind = "idle" | "converting" | "sending" | "finishing" | "done" | "failed";

export interface ClipProgressInput {
  phase: { kind: ClipsPhaseKind; done?: number; total?: number };
  /** The clips handed over, in order, by name. */
  names: readonly string[];
  /** The session once the plans are in; null before (and when the plan was refused). */
  session: Pick<ClipsSession, "manifests" | "jobs" | "already"> | null;
  jobs: ReadonlyMap<string, ClipJobState>;
  /** The listen for tags runs before the upload opens. */
  listening: boolean;
}

export function clipFileViews({ phase, names, session, jobs, listening }: ClipProgressInput): ClipFileView[] {
  if (listening) return names.map((name) => ({ id: name, label: name, bytes: 0, sent: 0, status: "listening" }));
  if (phase.kind === "idle") return [];
  if (phase.kind === "converting") {
    const done = phase.done ?? 0;
    return names.map((name, i) => ({ id: name, label: name, bytes: 0, sent: 0, status: i < done ? "queued" : i === done ? "converting" : "waiting" }));
  }
  if (!session) {
    // The plan was refused before anything was sent: every file, the one reason on the summary line.
    return names.map((name) => ({ id: name, label: name, bytes: 0, sent: 0, status: phase.kind === "failed" ? "failed" : "waiting" }));
  }
  const already = new Set(session.already);
  return session.manifests.map((m) => {
    const job = session.jobs.find((j) => j.hash === m.hash);
    if (already.has(m.name)) return { id: m.name, label: m.name, bytes: m.sizeBytes, sent: m.sizeBytes, status: "already" };
    if (!job) {
      // The pool held these bytes: nothing to send, the rest as the others.
      const status: ClipFileStatus = phase.kind === "finishing" ? "checking" : phase.kind === "done" ? "added" : "sent";
      return { id: m.name, label: m.name, bytes: m.sizeBytes, sent: m.sizeBytes, status };
    }
    const state = jobs.get(job.id);
    const bytes = job.bytes.byteLength;
    if (!state || state.status === "queued") return { id: job.id, label: m.name, bytes, sent: 0, status: "queued" };
    if (state.status === "failed") return { id: job.id, label: m.name, bytes, sent: state.sent, status: "failed", ...(state.reason ? { reason: state.reason } : {}) };
    if (state.status === "sending") return { id: job.id, label: m.name, bytes, sent: state.sent, status: "sending" };
    const status: ClipFileStatus = phase.kind === "finishing" ? "checking" : phase.kind === "done" ? "added" : "sent";
    return { id: job.id, label: m.name, bytes, sent: bytes, status };
  });
}

/** Bytes still to send and the words for the summary line. */
export function clipProgressTotals(views: readonly ClipFileView[]): { sentBytes: number; totalBytes: number; failed: number; active: boolean } {
  let sentBytes = 0;
  let totalBytes = 0;
  let failed = 0;
  let active = false;
  for (const v of views) {
    if (v.status === "already") continue;
    sentBytes += v.sent;
    totalBytes += v.bytes;
    if (v.status === "failed") failed++;
    if (v.status === "sending" || v.status === "queued" || v.status === "converting" || v.status === "listening" || v.status === "checking" || v.status === "waiting") active = true;
  }
  return { sentBytes, totalBytes, failed, active };
}

/** The word a row shows beside its name. */
export function clipStatusText(v: ClipFileView): string {
  switch (v.status) {
    case "waiting":
      return "Waiting";
    case "listening":
      return "Listening…";
    case "converting":
      return "Converting…";
    case "queued":
      return "Queued";
    case "sending":
      return v.bytes > 0 ? `${Math.min(99, Math.round((v.sent / v.bytes) * 100))}%` : "Sending…";
    case "sent":
      return "Sent";
    case "already":
      return "Already here";
    case "checking":
      return "Checking…";
    case "added":
      return "Added";
    case "failed":
      return v.reason ? `Failed — ${v.reason}` : "Failed";
  }
}
